import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChatEntry } from 'dragonmouse-shared/interfaces';
// It is actually quite intricate to get the scroll behavior we want:
// When the user submits a prompt, we scroll so it's at the top of the screen (leaving space underneath)
// The space persists -- meaning that we can scroll up and down and it remains
// Well the space persists until the system's chat responses appear -- they responses gobble up the empty space!
// If the system's responses don't fill the empty space, a little bit of whitespace is left, the user can
// scroll up and down and that little bit of whitespace remains.
//
// This is the standard but implementing it is pretty intricate
// To get the scroll view to scroll we need to manually inject some space -- outside of the React loop
// so it can all happen before the next render. That's why I use useLayoutEffect.
//
// I talked to some LLMs who were like "here's the easy trick everyone uses" but it always involved naively
// calling scrollIntoView without adding empty space. No variation of that worked for me. But doing the math
// directly here is working very robustly at present -- ss
//
// I did have to use this magic number which is needed to avoid adding a bit TOO much space.
// Perhaps it's needed because padding isn't included in some computations?
// I didn't dig in too much because it's working well and I don't care much beyond that until is starts to fail (sorry) - ss
const MAGIC_NUMBER = 18;

interface UseChatScrollOptions {
	containerRef: React.RefObject<HTMLDivElement>;
	spacerRef: React.RefObject<HTMLDivElement>;
	entries: ChatEntry[];
	activeChatId: string | null;
	lastChatMessage: string | null;
	shouldSkipAutoScroll?: () => boolean;
	isInitialized?: boolean; // Whether parent is ready for initial scroll on load
}

interface UseChatScrollResult {
	isScrolledToBottom: boolean;
	onJumpToBottom: () => void;
}

export function useChatScroll({
	containerRef,
	spacerRef,
	entries,
	activeChatId,
	lastChatMessage,
	shouldSkipAutoScroll,
	isInitialized,
}: UseChatScrollOptions): UseChatScrollResult {
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const originalSpacerHeight = useRef<number>(0);
	const spacerHeight = useRef<number>(0);

	const entriesLength = entries?.length ?? 0;

	// If last message is a user message, scroll so user message appears at top of chat panel.
	// IMPORTANT: Only depend on entriesLength, NOT on entries array itself. The entries array
	// gets a new reference on every Redux update (even when content is unchanged), which would
	// cause spurious scroll-to-top behavior. We only want this to fire when a new entry is added.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (!entries || entriesLength === 0) return;

		if (entries[entriesLength - 1].kind === 'user') {
			const messages = containerRef.current?.querySelectorAll('.user-message');
			const lastUserMessage = messages?.[messages.length - 1];
			if (lastUserMessage) {
				// make the spacer's height be that of the visible area minus that of the user message
				const visibleAreaHeight = containerRef.current?.clientHeight;
				const userMessageHeight = lastUserMessage.clientHeight;
				if (visibleAreaHeight && spacerRef.current) {
					const H = visibleAreaHeight - userMessageHeight - MAGIC_NUMBER;
					spacerRef.current.style.height = `${H}px`;
					spacerHeight.current = H;
					// store this so we can chip away at it as the last system message grows
					originalSpacerHeight.current = H;
				}
				// since we forcibly sized the spacer, voila! our scrolling works
				if (containerRef.current && lastUserMessage) {
					// Get the offsetTop of the message relative to the container
					const messageOffset = (lastUserMessage as HTMLElement).offsetTop;
					containerRef.current.style.scrollBehavior = 'smooth';
					containerRef.current.scrollTop = messageOffset;
				}
			}
		}
	}, [entriesLength]);

	// Whenever the last entry is a system message, eat away at the spacer height as system message grows.
	// This runs regardless of scroll position - we're just reducing empty space, not moving content.
	// IMPORTANT: Only depend on lastChatMessage and entriesLength, NOT on entries array itself.
	// Same reasoning as above - entries array reference changes on every Redux update, causing
	// spurious spacer adjustments and unwanted scroll behavior.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (!entries || entriesLength === 0) return;
		if (spacerHeight.current > 0 && entries[entriesLength - 1].kind === 'system') {
			const messages = containerRef.current?.querySelectorAll('.system-message');
			const lastSystemMessage = messages?.[messages.length - 1];
			if (lastSystemMessage) {
				const systemMessageHeight = lastSystemMessage.clientHeight;
				if (systemMessageHeight && spacerRef.current) {
					// remove the height that's been added by the system message
					const newSpacerHeight = Math.max(originalSpacerHeight.current - systemMessageHeight, 0);
					spacerHeight.current = newSpacerHeight;
					spacerRef.current.style.height = `${spacerHeight.current}px`;
				}
			}
		}
	}, [lastChatMessage, entriesLength]);

	// When chat id changes, scroll to the bottom (unless shouldSkipAutoScroll returns true).
	// Wait for initialization to ensure scene metadata is loaded before scrolling.
	// IMPORTANT: Omit shouldSkipAutoScroll from deps - it's a callback that gets recreated each render,
	// but we only want to scroll when chatId or initialization state actually changes.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return; // Wait for scene/design system to be initialized
		if (containerRef.current) {
			spacerHeight.current = 0;
			if (spacerRef.current) {
				// if the prior chat left a spacer, clear it
				spacerRef.current.style.height = `${spacerHeight.current}px`;
			}
			// disable smooth scrolling
			containerRef.current.style.scrollBehavior = 'auto';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}
	}, [activeChatId, isInitialized]);

	// Scroll to bottom when scene/design system first loads. The 500ms delay accounts for
	// thumbnails and other async content that affects scroll height. Not perfect but helps.
	// IMPORTANT: Only depend on isInitialized, not shouldSkipAutoScroll (recreated each render).
	// containerRef is a stable ref and doesn't need to be a dependency.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return;
		if (containerRef.current) {
			setTimeout(() => {
				if (containerRef.current) {
					containerRef.current.style.scrollBehavior = 'auto';
					containerRef.current.scrollTop = containerRef.current.scrollHeight;
					setIsScrolledToBottom(true);
				}
			}, 500);
		}
	}, [isInitialized]);

	// Add scroll event listener to track scroll position.
	// Empty deps array is intentional - we capture containerRef.current once on mount.
	// The ref object is stable, and we only need to set up the listener once.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			// instead of 1 we use 2 as a test to account for floating point precision issues
			const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;

			setIsScrolledToBottom(isAtBottom);
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, []);

	// As messages stream in, update isScrolledToBottom state.
	// Only depend on message length changing, not containerRef (stable ref).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (lastChatMessage?.length && containerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
			const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
			setIsScrolledToBottom(isAtBottom);
		}
	}, [lastChatMessage?.length]);

	const onJumpToBottom = () => {
		if (containerRef.current) {
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}
	};

	return {
		isScrolledToBottom,
		onJumpToBottom,
	};
}
